import { Component } from '@theme/component';

const HISTORY_KEY = 'moksh:recently-viewed:v1';
const HISTORY_LIMIT = 8;
const HISTORY_AGE = 30 * 24 * 60 * 60 * 1000;
const validHandle = (value) => typeof value === 'string' && /^[\p{L}\p{N}_-]{1,255}$/u.test(value);

class MokshDiscovery extends Component {
  #controller;
  #observer;
  #history = [];

  connectedCallback() {
    super.connectedCallback();
    if (this.dataset.recent !== 'true') return;
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.querySelector('[data-clear-recent]')?.addEventListener('click', () => this.#clear(), { signal: this.#controller.signal });
    this.#history = this.#readHistory().filter((entry) => entry.handle !== this.dataset.handle);
    if (validHandle(this.dataset.handle)) {
      this.#writeHistory([{ handle: this.dataset.handle, viewedAt: Date.now() }, ...this.#history].slice(0, HISTORY_LIMIT));
    }
    if (!this.#history.length) return;
    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer.disconnect();
        this.#loadRecent();
      }, { rootMargin: '400px' });
      this.#observer.observe(this);
    } else {
      this.#loadRecent();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#observer?.disconnect();
  }

  #readHistory() {
    try {
      const stored = JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
      if (!Array.isArray(stored)) return [];
      const seen = new Set();
      const now = Date.now();
      return stored.filter((entry) => {
        if (!entry || !validHandle(entry.handle) || seen.has(entry.handle)) return false;
        if (!Number.isFinite(entry.viewedAt) || entry.viewedAt > now || now - entry.viewedAt > HISTORY_AGE) return false;
        seen.add(entry.handle);
        return true;
      }).sort((a, b) => b.viewedAt - a.viewedAt).slice(0, HISTORY_LIMIT);
    } catch {
      return [];
    }
  }

  #writeHistory(entries) {
    try { localStorage.setItem(HISTORY_KEY, JSON.stringify(entries)); } catch { }
  }

  #clear() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#history = [];
    try { localStorage.removeItem(HISTORY_KEY); } catch { }
    const group = this.querySelector('[data-recent-group]');
    const relatedHeading = this.querySelector('.md-discovery__group:not([data-recent-group]) h2');
    const heading = relatedHeading || document.querySelector('main h1');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }
    if (group) group.hidden = true;
    this.querySelector('[data-recent-grid]')?.replaceChildren();
  }

  async #loadRecent() {
    const signal = this.#controller.signal;
    const root = this.dataset.root || window.Shopify?.routes?.root || '/';
    const products = await Promise.all(this.#history.map(async ({ handle }) => {
      try {
        const url = new URL(`${root}products/${encodeURIComponent(handle)}.js`, window.location.origin);
        const response = await fetch(url, { signal, credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) return null;
        const item = await response.json();
        if (!item.available || item.handle === this.dataset.handle || !validHandle(item.handle)) return null;
        const variant = item.variants?.find((candidate) => candidate.available && candidate.price > 0);
        return variant ? { item, variant } : null;
      } catch { return null; }
    }));
    if (signal.aborted || !this.isConnected) return;
    const grid = this.querySelector('[data-recent-grid]');
    const group = this.querySelector('[data-recent-group]');
    if (!grid || !group) return;
    const cards = products.filter(Boolean).slice(0, 4).map(({ item, variant }) => this.#card(item, variant));
    grid.replaceChildren(...cards);
    group.hidden = cards.length === 0;
  }

  #money(amount) {
    try {
      return new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang || 'en-IN', {
        style: 'currency', currency: this.dataset.currency || 'INR'
      }).format(amount / 100);
    } catch {
      return `${(amount / 100).toFixed(2)} ${this.dataset.currency || 'INR'}`;
    }
  }

  #card(item, variant) {
    const node = (tag, className, text) => {
      const element = document.createElement(tag);
      if (className) element.className = className;
      if (text != null) element.textContent = text;
      return element;
    };
    const card = node('li', 'md-discovery-card');
    const link = node('a', 'md-discovery-card__link');
    link.href = `${this.dataset.root || '/'}products/${encodeURIComponent(item.handle)}`;
    const media = node('div', 'md-discovery-card__image');
    if (item.featured_image) {
      try {
        const source = new URL(item.featured_image, window.location.origin);
        if (source.protocol === 'https:' || source.origin === window.location.origin) {
          const sized = (width) => { const url = new URL(source); url.searchParams.set('width', width); return url.href; };
          const image = node('img');
          image.src = sized(320);
          image.srcset = [160, 320, 480, 640].map((width) => `${sized(width)} ${width}w`).join(', ');
          image.sizes = '(min-width: 1440px) 300px, (min-width: 750px) 23vw, 45vw';
          image.width = 640;
          image.height = 640;
          image.alt = '';
          image.loading = 'lazy';
          image.decoding = 'async';
          media.append(image);
        }
      } catch { }
    }
    const price = node('div', 'md-discovery-card__price');
    if (variant.compare_at_price > variant.price) {
      const regular = node('s', '', this.#money(variant.compare_at_price));
      regular.setAttribute('aria-label', this.dataset.regularLabel);
      price.append(regular);
    }
    const current = node('strong', '', this.#money(variant.price));
    current.setAttribute('aria-label', this.dataset.saleLabel);
    price.append(current);
    const view = node('span', 'md-discovery-card__view', this.dataset.viewLabel);
    const arrow = node('span', '', '↗');
    arrow.setAttribute('aria-hidden', 'true');
    view.append(arrow);
    link.append(media, node('h3', '', item.title), price, view);
    card.append(link);
    return card;
  }
}

if (!customElements.get('moksh-discovery')) customElements.define('moksh-discovery', MokshDiscovery);
